import { SwirlPdf } from '../../../../icons-library/swirl-pdf';
import { Text } from '../../../../../ui-components/text/text';
import { Show, useContext } from 'solid-js';
import { AppContext } from '../../../../../app-context-provider/app-context-provider';
import { gql } from 'graphql-request';
import { AtosLogoPdfFooter } from '../../../../icons-library/atos-logo-pdf-footer';
import { StyledBlankPageFooter, StyledFooterItems, StyledFooterItem, StyledFooterPostTitle, StyledCopyrightItems, StyledPdfDivider, StyledFooterLogo, StyledFooterLogoInner, StyledFooterSwirl, StyledFooterTexts, StyledQrCodeFooter } from './pdf-footer.style';
import { AtosCareLogoPdfFooter } from '../../../../icons-library/atos-care-logo-pdf-footer';
import { generateQRCode } from '../generate-qr';
import { useLocation } from '@solidjs/router';

const GET_FOOTER_QUERY = gql`
    query GetFooter($key: String!) {
        footer: resource(key: $key) {
            content
            key
        }
    }
`;

type FooterPdfProps = {
    postTitle: string;
    pageNumber: number;
    finalPage: boolean;
    url: string;
    totalPageCount: number;
    postType?: string;
}


export const FooterPDF = (props: FooterPdfProps) => {
    const { 
        createCachedResource,
        localize,
        siteInfo,
        productsEntityName,
        eventsEntityName,
        blogAndNewsEntityName,
    } = useContext(AppContext);

    const location = useLocation();
    
    const [ footerContent ] = createCachedResource(GET_FOOTER_QUERY, () => ({ key: '/wp-json/rawb/v1/footer' }), true);

    const parsedCopyrightText = () => {
        const text = footerContent()?.footer?.content?.copyrighttext;
        if (!text) return '';
        const match = text.match(/\{\{(.*?)\}\}/);
        if (!match) return text;
        const currentYear = new Date().getFullYear();
        return text.replace(match[0], currentYear.toString());
    };


    const getUrl = () => {
        let newUrl = props.url;

        switch (props.postType) {
            case 'product':
                newUrl = newUrl.replace(/^\/product/, `/${productsEntityName}`);
                break;
            case 'blog-and-news':
                newUrl = newUrl.replace(/^\/blog-and-news/, `/${blogAndNewsEntityName}`);
                break;
            case 'event':
                newUrl = newUrl.replace(/^\/event/, `/${eventsEntityName}`);
                break;
        }

        if (location.search) {
            newUrl = `${newUrl}${location.search}`;
        }
        
        const prefix = siteInfo.protocol === 'https' ? 'https://' : 'http://';
        let niceUrl = `${ siteInfo.currentDomain }${ newUrl }`;

        if (siteInfo.target === 'live' && !niceUrl.startsWith('www.')) {
            niceUrl = `www.${ niceUrl }`;
        }

        if (niceUrl.includes('?data=')) {
            // Remove "?data=" and everything after it - the url when going via pdf service will have that data attribute which we need to remove
            niceUrl = niceUrl.replace(/\?data=.*$/, '');
        }

        const fullUrl = `${prefix}${niceUrl}`;

        return fullUrl;
    };

    return (
        <StyledBlankPageFooter>
            <StyledFooterItems>

                <Show when={props.finalPage}>
                    <StyledQrCodeFooter innerHTML={generateQRCode(getUrl())} />
                </Show>

                <StyledFooterTexts>
                    <StyledFooterItem>
                        <StyledFooterPostTitle>
                            <Text fontSize="smallerPDF" displayRedVerticalLine={false} noBlockSpacing={false} color='black'>
                                {props.postTitle}
                            </Text>
                        </StyledFooterPostTitle>
                    </StyledFooterItem>
                    <StyledFooterItem>
                        <Text fontSize="smallerPDF" displayRedVerticalLine={false} noBlockSpacing={false} color='black'>
                            { getUrl() }
                        </Text>
                    </StyledFooterItem>
                    <StyledCopyrightItems>
                        <Text fontSize="tinyPDF" displayRedVerticalLine={false} noBlockSpacing={true} color='black'>
                            {parsedCopyrightText()}
                        </Text>

                        <StyledPdfDivider />

                        <Text fontSize="tinyPDF" displayRedVerticalLine={false} noBlockSpacing={true} color='black'>
                            {`${localize('page', 'Page')} ${props.pageNumber} ${localize('of', 'of')} ${props.totalPageCount}`}
                        </Text>
                    </StyledCopyrightItems>
                </StyledFooterTexts>

                <StyledFooterLogo>
                    <StyledFooterLogoInner>
                        <Show 
                            when={siteInfo?.siteType === 'atos-care'}
                            fallback={(
                                <AtosLogoPdfFooter width={props.finalPage ? 9.35 : 5.6} height={props.finalPage ? 4.55 : 2.72} />
                            )}
                        >
                            <AtosCareLogoPdfFooter width={props.finalPage ? 9.35 : 5.6} height={props.finalPage ? 4.55 : 2.72}/>
                        </Show>
                    </StyledFooterLogoInner>
                </StyledFooterLogo>
            </StyledFooterItems>

            <Show when={siteInfo?.siteType !== 'atos-care' && props.finalPage}>
                <StyledFooterSwirl>
                    <SwirlPdf />
                </StyledFooterSwirl>
            </Show>
        </StyledBlankPageFooter>
    );
};